import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as RsComponents } from 'react-select';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { StatusUpdate, StatusUpdateState } from 'api/status-updates';
import { CreatableSelect } from 'components/atoms/select';
import { isValidEmail } from 'utils/is-valid-email';
import { useActiveStatUpdateCCs } from './cc-hooks';

export type EmailVal = {
  label: string;
  value: string;
};
interface Props {
  activeStatusUpdate: StatusUpdate;
  onEmailsAdd: (emails: EmailVal[]) => void;
  ccOptions: EmailVal[];
  userEmails: EmailVal[];
  setUserEmails: (users) => void;
  isDisabled: boolean;
}

const ESelect = styled(CreatableSelect)`
  width: 100%;
`;

const SelectWrapper = styled.div`
  width: 100%;
`;

const StyledControl = styled(RsComponents.Control)<{ isPublished: boolean }>`
  border: none;
  padding: 0;
  background-color: transparent !important;
  cursor: text;
  min-height: 0;
  border-bottom: 1px solid transparent !important;

  &:hover {
    border-color: var(--border-color) !important;
  }

  ${ifProp(
    'isFocused',
    css`
      box-shadow: none;
      border-color: var(--border-color) !important;
      outline-color: transparent !important;
    `,
  )};

  ${ifProp(
    'isPublished',
    css`
      && {
        border-color: transparent !important;
      }
    `,
  )};
`;

const Container = styled(RsComponents.ValueContainer)`
  padding: 0;
`;

const Control = (props, isPublished) => (
  <StyledControl isPublished={isPublished} {...props} />
);

const ValueContainer = (props) => <Container {...props} />;

export const EmailInviteSelect: FC<Props> = ({
  activeStatusUpdate,
  onEmailsAdd,
  ccOptions,
  userEmails,
  setUserEmails,
  isDisabled,
}) => {
  const ccEmails = useActiveStatUpdateCCs();
  const isPublished = activeStatusUpdate?.state === StatusUpdateState.Published;

  const onChange = (mails) => {
    setUserEmails(mails || []);

    const allEmails = isPublished
      ? ccEmails.map((email) => {
          return {
            label: email,
            value: email,
          };
        })
      : [];

    onEmailsAdd([...allEmails, ...(mails ?? [])]);
  };

  const isANewMailId = (mailId: string) => {
    return !ccEmails.includes(mailId);
  };

  const getCreateLabel = () => (
    <FormattedMessage id="su.cc.email_form.create" />
  );

  const getNoOptionsMessage = ({ inputValue }) => {
    if (ccEmails.includes(inputValue)) {
      if (isPublished) {
        return (
          <FormattedMessage
            id="su.cc.modal.published_emails"
            values={{ emails: inputValue }}
          />
        );
      }

      return (
        <FormattedMessage
          id="su.cc.modal.already_added_email"
          values={{ emails: inputValue }}
        />
      );
    }

    return null;
  };

  const isInputValid = (input) =>
    isValidEmail(input.trim()) && isANewMailId(input.trim());

  return (
    <SelectWrapper data-cy="status_update_cc_email_form">
      <ESelect
        isDisabled={isDisabled}
        placeholder="Add emails"
        components={{
          ValueContainer,
          Control: (props) => Control(props, isPublished),
        }}
        showMenuOnOpen={false}
        isMulti
        type="user"
        isClearable
        options={[]}
        noOptionsMessage={getNoOptionsMessage}
        formatCreateLabel={getCreateLabel}
        defaultValue={ccOptions}
        isValidNewOption={isInputValid}
        onChange={onChange}
        value={userEmails}
      />
    </SelectWrapper>
  );
};
